// src/composables/useRoomPrice.js
import { computed, ref } from 'vue'

/**
 * useRoomPrice(formRef, roomsRef, sourceUuidRef)
 *
 * - formRef: ref yang berisi reservation form (pakai field `start`, `end`, `room_uuid`, `source_uuid`)
 * - roomsRef: ref yang berisi array rooms (hasil GET /api/rooms)
 * - sourceUuidRef: ref/computed yang berisi source_uuid yang dipilih (reactive)
 *
 * Return:
 * - roomPrice (computed) => TOTAL price untuk keseluruhan stay (jumlah semua malam)
 * - totalNights (computed)
 * - roomPriceDetails (computed) => { total, averagePerNight, nightly: [{date, season, rate}], totalNights, highNights, lowNights }
 */
// export function useRoomPrice(formRef, roomsRef, sourceUuidRef) {
export function useRoomPrice(formRef, roomsRef) {
  const nightlyRaw = ref([])

  // --- season ranges (mm-dd) repeating setiap tahun
  // ubah/extend sesuai rule hotelmu
  const HIGH_RANGES = [
    { start: '07-01', end: '09-15' }, // contoh high summer
    { start: '12-22', end: '01-01' }  // example: libur akhir tahun (melintasi tahun)
  ]

  function mdNumberFromString(mmdd) {
    const [mm, dd] = mmdd.split('-').map(s => parseInt(s, 10))
    return mm * 100 + dd
  }
  function mdNumberFromDate(d) {
    const mm = d.getMonth() + 1
    const dd = d.getDate()
    return mm * 100 + dd
  }
  function isMdInRange(md, s, e) {
    if (s <= e) return md >= s && md <= e
    // range melintasi tahun, contoh 12-15 sampai 01-05
    return md >= s || md <= e
  }

  const highRangesNum = HIGH_RANGES.map(r => ({
    s: mdNumberFromString(r.start),
    e: mdNumberFromString(r.end)
  }))

  function getSeasonByDate(date) {
    const md = mdNumberFromDate(new Date(date))
    if (highRangesNum.some(r => isMdInRange(md, r.s, r.e))) return 'high'
    return 'low'
  }

  // total nights (integer)
  const totalNights = computed(() => {
    if (!formRef.value?.start || !formRef.value?.end) return 0
    const s = new Date(formRef.value.start)
    const e = new Date(formRef.value.end)
    if (isNaN(s) || isNaN(e) || e <= s) return 0
    return Math.round((e - s) / (1000 * 60 * 60 * 24))
  })

  // main computed total price (sum per-night rates)
  const roomPrice = computed(() => {
    // minimal checks
    if (
      !formRef.value?.room_uuid ||
      !formRef.value?.start ||
      !formRef.value?.end ||
      // !formRef.value?.source_uuid
      !formRef.value?.source_type
    ) {
      nightlyRaw.value = []
      return 0
    }

    const rooms = (roomsRef && roomsRef.value) ? roomsRef.value : []
    const selectedRoom = rooms.find(r => String(r.uuid) === String(formRef.value.room_uuid))
    if (!selectedRoom || !selectedRoom.room_category || !selectedRoom.room_category.prices) {
      nightlyRaw.value = []
      return 0
    }

    // // choose sourceUuid from provided ref (reactive) or fallback to form
    // const sourceUuid = (sourceUuidRef && typeof sourceUuidRef.value !== 'undefined' && sourceUuidRef.value !== null)
    //   ? sourceUuidRef.value
    //   : formRef.value.source_uuid

    // const sourceUuid = formRef.value.source_uuid
    //**diganti ke */
    const sourceType = formRef.value.source_type



    const start = new Date(formRef.value.start)
    const end = new Date(formRef.value.end)
    if (isNaN(start) || isNaN(end) || end <= start) {
      nightlyRaw.value = []
      return 0
    }

    let total = 0
    const nightly = []

    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      const isoDate = new Date(d).toISOString().slice(0, 10)
      const season = getSeasonByDate(d)

      // //**versi lama */
      // // cari price entry di room_category.prices
      // const priceEntry = selectedRoom.room_category.prices.find(p =>
      //   String(p.season) === String(season) &&
      //   // String(p.source_uuid) === String(sourceUuid)
      //   String(p.source.type) === String(sourceType)
      // )

      const priceEntry = selectedRoom.room_category.prices.find(p =>
        String(p.season) === String(season) &&
        String(p.source_type) === String(sourceType)
      )
      

      const rate = priceEntry ? Number(priceEntry.price) : 0
      total += rate

      nightly.push({ date: isoDate, season, rate })
    }

    nightlyRaw.value = nightly
    return total
  })

  const roomPriceDetails = computed(() => {
    const nightly = nightlyRaw.value || []
    const total = nightly.reduce((s, n) => s + (n.rate || 0), 0)
    const avg = nightly.length ? total / nightly.length : 0
    const high = nightly.filter(n => n.season === 'high').length
    const low = nightly.length - high
    return {
      total,
      averagePerNight: avg,
      nightly,
      totalNights: nightly.length,
      highNights: high,
      lowNights: low
    }
  })

  return {
    roomPrice,
    totalNights,
    roomPriceDetails
  }
}
